import * as Action from '../actions/PageAction';
import { Publication, LinearSpineItem, SpineItem } from '../types/epub';
import { getPrevPageForTab, getNextPageForTab, getReference } from '../helpers/pages';
import { getDefaultRendition, isLinearItemPath } from '../utils/epub';
import { findIndex } from '../../../../common/src/ts/utils';
import { joinReference } from '../helpers/uievent';
import { SizeConstraint } from 'ark-common';

export enum TabType {
  LinearPages,
  ExtraPage,
  ReflowablePages,
  GeneratedContent,
}

interface TabInfoBase {
  readonly id: string;
  readonly title: string;
  readonly focus: string; // popup id or "self"
  readonly favicon?: string | null;
}

export interface TabInfoLinearPages extends TabInfoBase {
  readonly type: typeof TabType.LinearPages;
  readonly currentPage: number;
}

interface TabInfoExtraContent extends TabInfoBase {
  readonly type: typeof TabType.ExtraPage;
  readonly path: string;
}

interface TabInfoReflowablePages extends TabInfoBase {
  readonly type: typeof TabType.ReflowablePages;
  readonly currentPage: number;
}

interface TabInfoGeneratedContent extends TabInfoBase {
  readonly type: typeof TabType.GeneratedContent;
  readonly key: string;
  readonly xhtml: string;
  readonly params: any;
}

export type TabInfo =
  TabInfoLinearPages | TabInfoExtraContent | TabInfoReflowablePages | TabInfoGeneratedContent;

export type PageableTabInfo = TabInfoLinearPages | TabInfoReflowablePages;

export const isPageableTabInfo = (tabInfo: TabInfo): tabInfo is PageableTabInfo => {
  return ('currentPage' in tabInfo);
};

export const isEnableGoToReference = (
  reference: string, tab: TabInfo, publication: Publication,
): boolean => {
  if (tab.type !== TabType.LinearPages) {
    return false;
  }
  return findItemByBookmarkReference(reference, publication) !== null;
};

export const findItemByBookmarkReference = (
  reference: string, publication: Publication,
): LinearSpineItem | null => {
  // FIXME : 線形探索 CFIからのitem探索はマーカーと共通化したい
  const linearItems = getDefaultRendition(publication).linearItems;
  for (const item of linearItems) {
    const packageRef = getReference(item.linearIndex, publication);
    const itemRef = joinReference(packageRef[0], packageRef[1], 'epubcfi(/4)');
    if (reference === itemRef) {
      return item;
    }
  }

  return null;
};

export interface PopupInfo {
  readonly parent: string | null; // parent tab id
  readonly title: string;
  readonly path: string;
  readonly modal: boolean;
  readonly resizable: boolean;
  readonly titlebar: boolean;
  readonly frame: boolean;
  readonly constraint: SizeConstraint;
  readonly zIndex: number;
  readonly collapsed: boolean;

  // Internal use only
  readonly topLevel: boolean;
  readonly collapsible: boolean;
  readonly needsRestoreCurrentTool: boolean;
  readonly initialMargin: number;
}

export interface PagesState {
  readonly tabs: ReadonlyArray<TabInfo>;
  readonly activeTab: number;
  readonly cumulativeTabCount: number; // これまでに作られたタブ数の合計(ID採番のためにのみ利用)
  readonly frontmostModelessPopupZIndex: number; // 最前面のモードレスポップアップのz-index
  readonly popups: { readonly [id: string]: PopupInfo };
}

export const initPagesState = (): PagesState => ({
  tabs: [],
  activeTab: 0, // FIXME : PAGE_OPEN_MAIN_TAB must be called before "tabs[0]" is referenced.
  cumulativeTabCount: 0,
  frontmostModelessPopupZIndex: 0,
  popups: {},
});

export const tabFocusSelf = 'self';
export const composerPanelID = 'composerid';

export const isPopupPanelID = (panelID: string) => panelID.indexOf('popup') === 0;

// 自動生成のIDとユーザ指定keyが重複しないようにprefixを分ける
export const makeTabIDFromKey = (key: string) => `tabid-u-${key}`;

const makeNextTabID = (state: PagesState, key?: string): [string, number] => {
  const id = (
    key === undefined ? `tabid-${state.cumulativeTabCount}` : makeTabIDFromKey(key)
  );
  return [id, state.cumulativeTabCount + 1];
};

export const makePopupPanelID = (
  state: PagesState,
  tabID: string,
  key: string,
): [string, number] => {
  if (tabID == null || key == null) {
    throw new Error('Invalid argument: both tabID and key must be given.');
  }
  return [`popup-${tabID}-${key}`, state.frontmostModelessPopupZIndex + 1];
};

const addOrSetGeneratedContentTab = (
  state: PagesState,
  key: string,
  title: string,
  xhtml: string,
  params: any,
): PagesState => {
  const newTabs = state.tabs.slice();
  const [id, cumulativeTabCount] = makeNextTabID(state, key);
  const index = findIndex(newTabs, tab => tab.id === id);
  if (index >= 0) {
    newTabs[index] = {
      id,
      title,
      key,
      xhtml,
      params,
      focus: tabFocusSelf,
      type: TabType.GeneratedContent,
    };
    return { ...state, tabs: newTabs, activeTab: index };
  }
  newTabs.push({
    id,
    key,
    title,
    xhtml,
    params,
    focus: tabFocusSelf,
    type: TabType.GeneratedContent,
  });
  return {
    ...state,
    cumulativeTabCount,
    tabs: newTabs,
    activeTab: newTabs.length - 1,
  };
};

const findExistingTabIndex = (tabs: ReadonlyArray<TabInfo>, path: string, key?: string) => {
  if (key === undefined) {
    return findIndex(
      tabs,
      tab => tab.type === TabType.ExtraPage && tab.path === path,
    );
  }

  const id = makeTabIDFromKey(key);
  return findIndex(
    tabs,
    tab => tab.id === id,
  );
};

const getPopupItemSizeConstraint = (
  item: SpineItem, action: Readonly<Action.PageAction>,
): SizeConstraint => {
  const constraintFromAction = action.type === Action.PAGE_OPEN_POPUP
    ? {
      minWidth: action.minWidth || undefined,
      minHeight: action.minHeight || undefined,
      maxWidth: action.maxWidth || undefined,
      maxHeight: action.maxHeight || undefined,
    }
    : {
      minWidth: undefined,
      minHeight: undefined,
      maxWidth: undefined,
      maxHeight: undefined,
    };

  return {
    minWidth: constraintFromAction.minWidth || item.minWidth || 300,
    minHeight: constraintFromAction.minHeight || item.minHeight || 300,
    maxWidth: constraintFromAction.maxWidth || item.maxWidth || 1600,
    maxHeight: constraintFromAction.maxHeight || item.maxHeight || 1600,
  };
};

export const pages = (
  state: PagesState,
  publication: Publication,
  action: Action.PageAction,
): PagesState => {
  switch (action.type) {
    case Action.PAGE_OPEN_MAIN_TAB: {
      const [id, cumulativeTabCount] = makeNextTabID(state);
      const newTabs: TabInfo[] = [{
        id,
        type: TabType.LinearPages,
        title: action.title,
        currentPage: action.pageIndex,
        focus: tabFocusSelf,
      }];
      return { ...state, cumulativeTabCount, tabs: newTabs, activeTab: 0 };
    }
    case Action.PAGE_OPEN_TAB: {
      const newTabs = state.tabs.slice();
      const index = findExistingTabIndex(newTabs, action.path, action.key);
      if (index >= 0) {
        if (action.active) {
          return { ...state, activeTab: index };
        }
        return state;
      }

      const [id, cumulativeTabCount] = makeNextTabID(state, action.key);
      newTabs.push({
        id,
        type: TabType.ExtraPage,
        title: action.title,
        path: action.path,
        focus: tabFocusSelf,
        favicon: action.favicon,
      });
      const activeTab = action.active ? newTabs.length - 1 : state.activeTab;
      return { ...state, cumulativeTabCount, activeTab, tabs: newTabs };
    }
    case Action.PAGE_REPLACE_CONTENT: {
      if (isLinearItemPath(action.path, getDefaultRendition(publication))) {
        throw new Error('Invalid operation: Replacing with linear item is not supported.');
      }
      const { tabs, activeTab, popups } = state;
      const { title, path } = action;

      const activeTabInfo = tabs[activeTab];
      if (activeTabInfo.focus !== tabFocusSelf) {
        if (activeTabInfo.focus in state.popups) {
          // TODO: ポップアップ内でReplaceContentした時の動作を決める
          //
          // 今は最初にポップアップを開いた時のidやconstraint(minWidth/Height, maxWidth/Height)のまま
          // タイトルと中身(path)だけ入れ替わる

          return {
            ...state,
            popups: {
              ...popups,
              [activeTabInfo.focus]: { ...popups[activeTabInfo.focus], title, path },
            },
          };
        }
        throw new Error('Illegal state: the focused popup was not found.');
      }

      if (activeTabInfo.type !== TabType.ExtraPage) {
        throw new Error(
          'Invalid operation: Only replacement of contents of ExtraPage tab is supported.',
        );
      }
      const newTabInfo = { ...activeTabInfo, title, path };
      const newTabs = state.tabs.slice();
      newTabs.splice(state.activeTab, 1, newTabInfo);
      return { ...state, tabs: newTabs };
    }
    case Action.PAGE_OPEN_REFLOWABLE: {
      const newTabs = state.tabs.slice();
      const index = findIndex(newTabs, tab => tab.type === TabType.ReflowablePages);
      if (index >= 0) {
        newTabs[index] = {
          id: newTabs[index].id,
          type: TabType.ReflowablePages,
          title: action.title,
          currentPage: action.pageIndex,
          focus: tabFocusSelf,
        };
        return { ...state, tabs: newTabs, activeTab: index };
      }
      const [id, cumulativeTabCount] = makeNextTabID(state);
      newTabs.push({
        id,
        type: TabType.ReflowablePages,
        title: action.title,
        currentPage: action.pageIndex,
        focus: tabFocusSelf,
      });
      return { ...state, cumulativeTabCount, tabs: newTabs, activeTab: newTabs.length - 1 };
    }
    case Action.PAGE_OPEN_GENERATED_CONTENT:
      return addOrSetGeneratedContentTab(
        state,
        action.key,
        action.title,
        action.xhtml,
        action.params,
      );
    case Action.PAGE_CLOSE_TAB: {
      const index = findIndex(state.tabs, tab => tab.id === action.tabID);
      if (index === -1) {
        return state;
      }

      const activeTab = (state.activeTab >= index) ? state.activeTab - 1 : state.activeTab;
      return {
        ...state,
        activeTab,
        tabs: [...state.tabs.slice(0, index), ...state.tabs.slice(index + 1)],
      };
    }
    case Action.PAGE_SELECT_TAB:
      return { ...state, activeTab: action.index };
    case Action.PAGE_OPEN_POPUP: {
      const { popups, tabs } = state;
      if (action.id in popups) {
        return state; // TODO : activate popup
      }
      const tabIndex = findIndex(tabs, tab => tab.id === action.parent);

      const item = getDefaultRendition(publication).itemIndices[action.path];
      if (item === undefined) {
        throw new Error('Popup item is not found.');
      }
      const constraint = getPopupItemSizeConstraint(item, action);

      return {
        ...state,
        tabs: tabIndex >= 0
          ? [
            ...tabs.slice(0, tabIndex),
            { ...tabs[tabIndex], focus: action.id },
            ...tabs.slice(tabIndex + 1),
          ]
          : state.tabs,
        frontmostModelessPopupZIndex: action.zIndex,
        popups: {
          ...popups,
          [action.id]: {
            constraint,
            path: action.path,
            title: action.title,
            parent: action.parent,
            modal: action.modal,
            resizable: action.resizable,
            titlebar: action.titlebar,
            frame: action.frame,
            zIndex: action.zIndex,
            collapsed: action.collapsed || false,

            // Internal use only
            topLevel: action.topLevel || false,
            collapsible: action.collapsible !== undefined ? action.collapsible : true,
            needsRestoreCurrentTool: action.needsRestoreCurrentTool || false,
            initialMargin: action.initialMargin || 0,
          },
        },
      };
    }
    case Action.PAGE_CLOSE_POPUP: {
      if (!(action.id in state.popups)) {
        return state; // Nothing to do.
      }
      const tabIndex = findIndex(state.tabs, tab => tab.id === state.popups[action.id].parent);
      if (tabIndex === -1) {
        const popups = { ...state.popups };
        if (popups[action.id] === undefined) {
          throw new Error('Popup not found.');
        }
        delete popups[action.id];
        return { ...state, popups };
      }

      const popups = { ...state.popups };
      delete popups[action.id];
      return {
        ...state,
        popups,
        tabs: [
          ...state.tabs.slice(0, tabIndex),
          { ...state.tabs[tabIndex], focus: tabFocusSelf },
          ...state.tabs.slice(tabIndex + 1),
        ],
      };
    }
    case Action.PAGE_SET_POPUP_COLLAPSED: {
      if (!(action.id in state.popups)) {
        return state; // Nothing to do.
      }

      const popups = { ...state.popups };
      const target = popups[action.id];

      return {
        ...state,
        popups: {
          ...state.popups,
          [action.id]: {
            ...target, collapsed: action.collapsed,
          },
        },
      };
    }
    case Action.PAGE_SET_FOCUS: {
      const tab = state.tabs[state.activeTab];
      if (tab.focus === action.focus) {
        return state;
      }
      if (action.focus === tabFocusSelf) {
        return {
          ...state,
          tabs: [
            ...state.tabs.slice(0, state.activeTab),
            { ...tab, focus: action.focus },
            ...state.tabs.slice(state.activeTab + 1),
          ],
        };
      }
      if (action.focus in state.popups) {
        const popup = state.popups[action.focus];
        if (popup.parent === null) {
          return state;
        }

        const frontmostModelessPopupZIndex = state.frontmostModelessPopupZIndex + 1;

        const popups = {
          ...state.popups,
          [action.focus]: { ...popup, zIndex: frontmostModelessPopupZIndex },
        };

        return {
          ...state,
          frontmostModelessPopupZIndex,
          popups,
          tabs: [
            ...state.tabs.slice(0, state.activeTab),
            { ...tab, focus: action.focus },
            ...state.tabs.slice(state.activeTab + 1),
          ],
        };
      }
      throw new Error(`Invalid focus request ${action.focus}`);
    }
    case Action.PAGE_GO_PREV: {
      // TODO : Reduce duplicated codes.
      const { tabs, activeTab } = state;
      const prevPage = getPrevPageForTab(tabs[activeTab], publication);
      if (prevPage === null) {
        throw new Error('Invalid operation.');
      }
      const targetTab = { ...tabs[activeTab], currentPage: prevPage };
      const newTabs: TabInfo[] =
        [...tabs.slice(0, activeTab), targetTab, ...tabs.slice(activeTab + 1)];
      return { ...state, tabs: newTabs };
    }
    case Action.PAGE_GO_NEXT: {
      const { tabs, activeTab } = state;
      const nextPage = getNextPageForTab(tabs[activeTab], publication);
      if (nextPage === null) {
        throw new Error('Invalid operation.');
      }
      const targetTab = { ...tabs[activeTab], currentPage: nextPage };
      const newTabs: TabInfo[] =
        [...tabs.slice(0, activeTab), targetTab, ...tabs.slice(activeTab + 1)];
      return { ...state, tabs: newTabs };
    }
    case Action.PAGE_GO_TO: {
      const { tabs } = state;
      const { active, tab: activeTab } = action;
      const targetTab = { ...tabs[activeTab] };
      if (targetTab.type !== TabType.LinearPages) {
        throw new Error('Invalid operation.');
      }
      if (state.activeTab === action.tab && targetTab.currentPage === action.target) {
        return state;
      }
      targetTab.currentPage = action.target;
      const newTabs: TabInfo[] =
        [...tabs.slice(0, activeTab), targetTab, ...tabs.slice(activeTab + 1)];

      return {
        ...state,
        activeTab: active ? activeTab : state.activeTab,
        tabs: newTabs,
      };
    }
    case Action.PAGE_GO_TO_REFERENCE: {
      const { tabs, activeTab } = state;

      // TODO : 別タブにいても紙面タブへ切り替えつつ移動
      if (activeTab !== 0) {
        return state;
      }

      const targetTab = { ...tabs[activeTab] };
      if (targetTab.type !== TabType.LinearPages) {
        throw new Error('Invalid operation.');
      }

      const item = findItemByBookmarkReference(action.reference, publication);
      if (item !== null) {
        targetTab.currentPage = item.linearIndex;
        const newTabs: TabInfo[] =
          [...tabs.slice(0, activeTab), targetTab, ...tabs.slice(activeTab + 1)];
        return { ...state, tabs: newTabs };
      }

      throw new Error('Invalid operation.');
    }
  }
};
